import { cryptowatchrService } from './cryptowatchr';
import { twelvedataService } from './twelvedata';

export interface AssetPrice {
  id: string;
  symbol: string;
  name: string;
  current_price: number;
  price_change_24h: number;
  price_change_percentage_24h: number;
  last_updated: string;
  source_url?: string; // URL of the API request that provided this data
}

export interface HistoricalData {
  prices: [number, number][];
  market_caps: [number, number][];
  total_volumes: [number, number][];
}

/**
 * 统一的API服务
 * 实现主要API（CryptowatchR）和备用API（Twelvedata）的切换逻辑
 */
class ApiService {
  // API状态跟踪
  private primaryApiHealthy = true;
  private lastPrimaryApiError: Date | null = null;
  private readonly PRIMARY_API_COOLDOWN = 60000; // 1分钟后重试主要API

  /**
   * 获取加密货币价格
   * 优先使用CryptowatchR，失败时切换到Twelvedata
   */
  async getCryptoPrice(ids: string[]): Promise<AssetPrice[]> {
    // 如果主要API健康或冷却期已过，尝试使用主要API
    if (this.primaryApiHealthy || this.shouldRetryPrimaryApi()) {
      try {
        const result = await cryptowatchrService.getCryptoPrice(ids);
        // 成功则标记主要API为健康
        this.primaryApiHealthy = true;
        this.lastPrimaryApiError = null;
        return result;
      } catch (error) {
        console.warn('CryptowatchR API失败，切换到Twelvedata:', error);
        this.primaryApiHealthy = false;
        this.lastPrimaryApiError = new Date();
      }
    }

    // 使用备用API
    try {
      return await twelvedataService.getCryptoPrice(ids);
    } catch (error) {
      console.error('所有API都失败:', error);
      throw new Error('无法获取加密货币价格，所有API都不可用');
    }
  }

  /**
   * 获取历史数据
   * 优先使用CryptowatchR，失败时切换到Twelvedata
   */
  async getHistoricalData(id: string, days: number = 7): Promise<HistoricalData> {
    // 如果主要API健康或冷却期已过，尝试使用主要API
    if (this.primaryApiHealthy || this.shouldRetryPrimaryApi()) {
      try {
        const result = await cryptowatchrService.getHistoricalData(id, days);
        this.primaryApiHealthy = true;
        this.lastPrimaryApiError = null;
        return result;
      } catch (error) {
        console.warn('CryptowatchR历史数据API失败，切换到Twelvedata:', error);
        this.primaryApiHealthy = false;
        this.lastPrimaryApiError = new Date();
      }
    }

    // 使用备用API
    try {
      return await twelvedataService.getHistoricalData(id, days);
    } catch (error) {
      console.error('所有API都失败:', error);
      throw new Error('无法获取历史数据，所有API都不可用');
    }
  }

  /**
   * 获取股票价格
   * 使用Twelvedata（主要API不支持股票）
   */
  async getStockPrice(symbol: string): Promise<AssetPrice> {
    try {
      return await twelvedataService.getStockPrice(symbol);
    } catch (error) {
      console.error('获取股票价格失败:', error);
      throw error;
    }
  }

  /**
   * 获取指数价格
   * 优先使用 Financial Modeling Prep，失败时回退到 Twelvedata
   * 注意：此方法为旧 API，建议使用 unifiedApiService 以利用 provider 优先级和故障转移
   */
  async getIndexPrice(symbol: string): Promise<AssetPrice> {
    try {
      return await twelvedataService.getIndexPrice(symbol);
    } catch (error) {
      console.error('获取指数价格失败:', error);
      throw error;
    }
  }

  /**
   * 获取外汇汇率
   * 使用Twelvedata（主要API不支持外汇）
   */
  async getForexRate(from: string, to: string): Promise<AssetPrice> {
    try {
      return await twelvedataService.getForexRate(from, to);
    } catch (error) {
      console.error('获取外汇汇率失败:', error);
      throw error;
    }
  }

  /**
   * 获取汇率数值（向后兼容）
   */
  async getExchangeRate(from: string, to: string): Promise<number> {
    try {
      const rate = await this.getForexRate(from, to);
      return rate.current_price;
    } catch (error) {
      console.error('获取汇率失败:', error);
      throw error;
    }
  }

  /**
   * 检查是否应该重试主要API
   */
  private shouldRetryPrimaryApi(): boolean {
    if (!this.lastPrimaryApiError) {
      return true;
    }
    const now = new Date();
    const timeSinceLastError = now.getTime() - this.lastPrimaryApiError.getTime();
    return timeSinceLastError >= this.PRIMARY_API_COOLDOWN;
  }

  /**
   * 重置API状态（用于测试或手动重置）
   */
  resetApiStatus(): void {
    this.primaryApiHealthy = true;
    this.lastPrimaryApiError = null;
  }
}

export const apiService = new ApiService();

